import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { Body1 } from "@fluentui/react-components";
import { useEffect, useState } from "react";
import { CalculatePrecision } from "../../primitives/utils";
import { NumberInputPropertyLine } from "./inputPropertyLine";
import { PropertyLine } from "./propertyLine";
import { TextPropertyLine } from "./textPropertyLine";
/**
 * Runtime-neutral controlled editor for two-, three-, and four-component numeric values.
 * @param props The value, change callback, component adapter, and property-line presentation.
 * @returns The tensor property line.
 */
export const ControlledTensorPropertyLine = (props) => {
    const { adapter, min, max, unit, step, precision, valueConverter } = props;
    const converted = (value) => (valueConverter ? valueConverter.from(value) : value);
    const formatted = (value) => converted(value).toFixed(precision ?? (step !== undefined ? Math.max(0, CalculatePrecision(step)) : 2));
    const [value, setValue] = useState(props.value);
    useEffect(() => {
        setValue(props.value);
    }, [props.value, props.expandedContent]);
    const onComponentChange = (componentValue, component) => {
        const storedValue = valueConverter ? valueConverter.to(componentValue) : componentValue;
        const newValue = adapter.withComponent(value, component, storedValue);
        setValue(newValue);
        props.onChange(newValue);
    };
    const summary = adapter.components.map((component) => formatted(adapter.getComponent(props.value, component))).join(", ");
    return (_jsx(PropertyLine, { ...props, expandedContent: _jsxs(_Fragment, { children: [props.expandedContent, _jsx(TensorSliders, { value: value, adapter: adapter, min: min, max: max, unit: unit, step: step, precision: precision, disabled: props.disabled, converted: converted, onChange: onComponentChange })] }), children: _jsx(Body1, { children: `[${summary}]` }) }));
};
const TensorSliders = (props) => {
    const { value, adapter, min, max, unit, step, precision, disabled, converted, onChange } = props;
    return (_jsx(_Fragment, { children: adapter.components.map((component) => (_jsx(NumberInputPropertyLine, { label: component.toUpperCase(), value: converted(adapter.getComponent(value, component)), min: min, max: max, onChange: (componentValue) => onChange(componentValue, component), unit: unit, step: step, precision: precision, disabled: disabled }, component))) }));
};
export const RadiansToDegreesConverter = {
    from: (value) => (value * 180) / Math.PI,
    to: (value) => (value * Math.PI) / 180,
};
/**
 * Runtime-neutral controlled Euler rotation editor.
 * @param props The rotation value, runtime adapter, and display options.
 * @returns The rotation property line.
 */
export const ControlledRotationVectorPropertyLine = (props) => {
    const { useDegrees, ...rest } = props;
    return (_jsx(ControlledTensorPropertyLine, { ...rest, unit: useDegrees ? "°" : "rad", valueConverter: useDegrees ? RadiansToDegreesConverter : undefined, step: useDegrees ? 1 : 0.01, precision: useDegrees ? 1 : 2 }));
};
/**
 * Runtime-neutral controlled quaternion editor with optional Euler presentation.
 * @param props The quaternion value, runtime conversion adapter, and display options.
 * @returns The quaternion or Euler property line.
 */
export const ControlledQuaternionPropertyLine = (props) => {
    const { adapter, useEuler, useDegrees, ...rest } = props;
    const [quaternion, setQuaternion] = useState(props.value);
    useEffect(() => {
        setQuaternion(props.value);
    }, [props.value]);
    const onQuaternionChange = (value) => {
        setQuaternion(value);
        props.onChange(value);
    };
    if (useEuler) {
        const quaternionSummary = adapter.quaternion.components.map((component) => adapter.quaternion.getComponent(quaternion, component).toFixed(4)).join(", ");
        return (_jsx(ControlledTensorPropertyLine, { ...rest, nullable: false, ignoreNullable: false, value: adapter.toEuler(quaternion), adapter: adapter.euler, valueConverter: useDegrees ? RadiansToDegreesConverter : undefined, onChange: (value) => onQuaternionChange(adapter.fromEuler(value, quaternion)), unit: useDegrees ? "°" : "rad", step: useDegrees ? 1 : 0.01, precision: useDegrees ? 1 : 2, expandedContent: _jsx(TextPropertyLine, { label: "Quaternion", value: `[${quaternionSummary}]` }) }));
    }
    return (_jsx(ControlledTensorPropertyLine, { ...rest, nullable: false, value: quaternion, adapter: adapter.quaternion, onChange: onQuaternionChange, unit: useDegrees ? "°" : "rad", step: useDegrees ? 1 : 0.01, precision: useDegrees ? 1 : 2 }));
};
//# sourceMappingURL=vectorPropertyLineCore.js.map